import _ from 'lodash';
import moment from 'moment';
import globalStore from '$$globalStore';
import projectsModel from '$$projectModel';
import ganttViewModel from '$$ganttViewModel';
import helpers from './helpers';
import app from '$$app';

class ResourcesOverload {
  constructor() {
    this.worker = null;
    this._state = {
      overloadedResources: {},
      overloadedTasks: [],
    };

    this.refreshCacheTimer = null;
    this.calculateTimer = null;

    this.workerInProgress = false;
    this.restartAfterFinish = false;
  }

  init() {
    if (window.Worker) {
      let version = Date.now();

      if (GT.productVersion && GT.productVersion.buildVersion) {
        version = GT.productVersion.buildVersion;
      }

      this.worker = new Worker('/workers/overload_worker.js' + `?${version}`);

      this.worker.onmessage = event => {
        this.workerInProgress = false;
        this._saveWorkerData(event.data);

        if (this.restartAfterFinish) {
          this.restartAfterFinish = false;
          this.refreshCache();
        }
      };

      this.worker.onerror = event => {
        this.workerInProgress = false;
        this.restartAfterFinish = false;

        webix.message({ type: 'error', text: __('overload_calculating_error'), expire: 100000 });
      };

      this.refreshCache();
    }
  }

  getOverloadedResources({ taskId, resourceId, taskIds }) {
    if (resourceId && taskId) {
      return this._state.overloadedResources[resourceId] && this._state.overloadedResources[resourceId].taskIds.find(id => id === taskId);
    }

    if (resourceId && taskIds) {
      return !!_.intersection(this._state.overloadedResources[resourceId]?.taskIds, taskIds).length;
    }

    if (resourceId) {
      return this._state.overloadedResources[resourceId];
    }

    if (taskId) {
      return this._state.overloadedTasks.includes(+taskId);
    }
  }

  refreshCache() {
    clearTimeout(this.calculateTimer);

    this.calculateTimer = setTimeout(() => {
      const ganttIds = projectsModel.getAllProjects().map(o => o.gantt_id);

      if (!ganttIds.length) {
        return;
      }

      this._workerRun(helpers.prepareData(ganttIds), ganttIds);
    }, 200);
  }

  _workerRun(innerData = {}, ganttIds) {
    const projectData = ganttViewModel.getActiveViewData();
    let totalStart = null;
    let totalEnd = null;

    ganttIds.forEach(ganttId => {
      const totalData = globalStore.getters['tasksModel/getTotalEstimateDataForProject'](ganttId);

      if (!totalStart || totalData.start_date && (totalData.start_date.valueOf() < totalStart.valueOf())) {
        totalStart = moment(totalData.start_date).subtract(1, 'days').toDate(); // for case when overload day is the first day of all projects
      }

      if (!totalEnd || totalData.end_date && (totalData.end_date.valueOf() > totalEnd)) {
        totalEnd = totalData.end_date;
      }
    });

    totalEnd.setDate(totalEnd.getDate() + 1);

    if (!this.worker || !totalStart || !totalEnd) {
      webix.message({ type: 'error', text: __('workload_worker_shutdown_or_no_tasks'), expire: 10000 });

      return;
    }

    if (this.workerInProgress) {
      this.restartAfterFinish = true;

      return;
    }

    this.workerInProgress = true;
    this.worker.postMessage(_.cloneDeep({
      innerData,
      projectConfigs: helpers.prepareProjectConfigs(ganttIds),
      ganttConfig: {
        work_time: true,
        duration_unit: 'minute',
        start_on_monday: projectData.config.start_on_monday,
      },
      totalStart,
      totalEnd,
    }));
  }

  _saveWorkerData(overloadMap) {
    clearTimeout(this.refreshCacheTimer);

    this.refreshCacheTimer = setTimeout(() => {
      let tasksIds = [];

      _.values(overloadMap).forEach(o => {
        tasksIds = tasksIds.concat(o.taskIds);
      });

      this._state.overloadedTasks = _.uniq(tasksIds);
      this._state.overloadedResources = overloadMap;

      app.trigger('ganttview:render');
    }, 250);
  }
}

export default new ResourcesOverload();
